/**
 * Mar 03, 2026 · Pending Purchase Order Statement.
 *
 * Sister report to `/reports/pending-sales-order`. Rolls every open PO line
 * into a printable sheet with two tabs:
 *   1. PO — Line-wise pending (per PO, per RM)
 *   2. RM  — Cross-supplier aggregate keyed by RM code
 *
 * Feb 2026 · Supplier Filter — searchable Supplier dropdown at the top
 * scopes every table + KPI + PDF export to a single supplier so
 * procurement can email one PDF per vendor for follow-up.
 *
 * Endpoint: GET /api/reports/pending-purchase-order
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  PrinterIcon, DownloadSimpleIcon, ArrowsClockwiseIcon,
  MagnifyingGlassIcon, XIcon, CaretDownIcon, FilePdfIcon,
} from "@phosphor-icons/react";
import { exportToCSV } from "@/lib/csv";
import { COMPANY, getPrintCompany } from "@/config/company";

const fmt = (n, d = 2) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d });
const fmtQty = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const _todayIso = () => new Date().toISOString().slice(0, 10);

function deliveryStatus(dateStr) {
  if (!dateStr) return { label: "—", color: "text-zinc-600" };
  const days = Math.round((new Date(dateStr) - new Date(_todayIso())) / 86400000);
  if (days < 0)  return { label: `${-days}d overdue`, color: "text-red-400 font-bold" };
  if (days <= 7) return { label: `${days}d left`, color: "text-amber-400 font-semibold" };
  return { label: `${days}d`, color: "text-zinc-400" };
}

export default function PendingPurchaseStatement() {
  const [data, setData] = useState(null);
  const [suppliers, setSuppliers] = useState([]);   // Full master for contact lookup
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("po"); // 'po' | 'rm'
  const [riskFilter, setRiskFilter] = useState("all");
  const [supplierName, setSupplierName] = useState(""); // Selected supplier (empty = ALL)
  const [supplierPickerOpen, setSupplierPickerOpen] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [rep, sups] = await Promise.all([
        api.get("/reports/pending-purchase-order"),
        api.get("/suppliers/").catch(() => ({ data: [] })),
      ]);
      setData(rep.data);
      setSuppliers(sups.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load pending purchase orders");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Supplier list scoped to those with open PO lines — no dead options in
  // the picker. Sorted alphabetically for scan-ability.
  const supplierOptions = useMemo(() => {
    const names = new Set();
    for (const l of (data?.po_lines || [])) if (l.supplier_name) names.add(l.supplier_name);
    return [...names].sort();
  }, [data]);

  const supplierFiltered = useMemo(() => {
    const src = data?.po_lines || [];
    return supplierName ? src.filter(l => l.supplier_name === supplierName) : src;
  }, [data, supplierName]);

  // Look up the full supplier master record for the picked name so the
  // preview + PDF can show email / phone / GSTIN for direct follow-up.
  const selectedSupplier = useMemo(() => {
    if (!supplierName) return null;
    return suppliers.find(s => s.name === supplierName) || { name: supplierName };
  }, [supplierName, suppliers]);

  const filtered = useMemo(() => {
    if (!data) return { po_lines: [], rm_rows: [] };
    const needle = q.trim().toLowerCase();
    const day = 86400000, today = _todayIso();
    const daysTo = (row) => {
      const d = row.expected_date || row.earliest_expected_date || "";
      if (!d) return Number.POSITIVE_INFINITY;
      return Math.round((new Date(d) - new Date(today)) / day);
    };
    const passRisk = (r) => {
      if (riskFilter === "all") return true;
      const dt = daysTo(r);
      if (riskFilter === "overdue") return dt < 0;
      if (riskFilter === "due7")    return dt <= 7;
      if (riskFilter === "due30")   return dt <= 30;
      return true;
    };
    const match = (r) => passRisk(r) && (!needle || Object.values(r).some(v => String(v ?? "").toLowerCase().includes(needle)));

    const po_lines = supplierFiltered.filter(match);
    // Recompute the RM rollup from the supplier-scoped PO lines so RM
    // numbers agree with what's shown in the PO tab.
    let rm_rows;
    if (supplierName) {
      const acc = {};
      for (const r of po_lines) {
        const k = r.rm_code || r.rm_name;
        const b = acc[k] || {
          rm_code: r.rm_code, rm_name: r.rm_name, uom: r.uom,
          pending_qty: 0, pending_value: 0, order_count: 0,
          suppliers: new Set(), pos: new Set(),
          earliest_expected_date: r.expected_date || "",
        };
        b.pending_qty += Number(r.pending_qty || 0);
        b.pending_value += Number(r.pending_value || 0);
        b.order_count += 1;
        if (r.supplier_name) b.suppliers.add(r.supplier_name);
        if (r.po_no) b.pos.add(r.po_no);
        const d = r.expected_date || "";
        if (d && (!b.earliest_expected_date || d < b.earliest_expected_date)) b.earliest_expected_date = d;
        acc[k] = b;
      }
      rm_rows = Object.values(acc).map(b => ({
        ...b,
        suppliers: [...b.suppliers].sort(),
        pos: [...b.pos].sort(),
      })).filter(match).sort((a, b) => (a.earliest_expected_date || "~").localeCompare(b.earliest_expected_date || "~"));
    } else {
      rm_rows = (data.rm_rows || []).filter(match);
    }
    return { po_lines, rm_rows };
  }, [data, q, riskFilter, supplierFiltered, supplierName]);

  // KPI totals recompute from the supplier-scoped list so leadership sees
  // per-supplier numbers when a filter is on.
  const totals = useMemo(() => {
    if (!data) return {};
    const p = filtered.po_lines;
    // Open/Partial POs = distinct PO Nos whose status contains 'draft'
    // or 'partial' (case-insensitive). Falls through to any distinct
    // open PO when statuses are exotic so the tile never shows a bogus 0.
    const draftPartialSet = new Set();
    const anyOpenSet = new Set();
    for (const r of p) {
      if (r.po_no) anyOpenSet.add(r.po_no);
      const s = (r.status || "").toLowerCase();
      if (s.includes("draft") || s.includes("partial")) draftPartialSet.add(r.po_no);
    }
    const open_partial_po_count = draftPartialSet.size || anyOpenSet.size;
    if (!supplierName) return { ...(data.totals || {}), open_partial_po_count };
    return {
      po_line_count: p.length,
      po_pending_qty: p.reduce((a, r) => a + Number(r.pending_qty || 0), 0),
      po_pending_value: p.reduce((a, r) => a + Number(r.pending_value || 0), 0),
      rm_count: filtered.rm_rows.length,
      open_partial_po_count,
    };
  }, [data, supplierName, filtered]);

  const doPrint = () => {
    const sheet = document.getElementById("pp-print-sheet");
    if (!sheet) { window.print(); return; }
    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .map(n => n.outerHTML).join("\n");
    const w = window.open("", "_blank", "width=1200,height=800");
    if (!w) { toast.error("Popup blocked · allow popups to print in landscape"); return; }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Pending Purchase Order</title>${styles}
      <style>
        @page { size: A4 landscape; margin: 8mm; }
        html, body { background: #fff !important; color: #000 !important; margin: 0 !important; padding: 0 !important; }
        #pp-print-sheet { position: static !important; width: 100% !important; min-height: auto !important; box-shadow: none !important; padding: 4mm !important; background: #fff !important; color: #000 !important; }
        table { color: #000 !important; }
        table thead { background: #eee !important; color: #000 !important; }
        [class*="text-emerald"], [class*="text-amber"], [class*="text-sky"], [class*="text-white"], [class*="text-red"], .text-\\[var\\(--accent\\)\\] { color: #000 !important; }
      </style>
    </head><body>${sheet.outerHTML}</body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); w.close(); }, 400);
  };

  // Server-side PDF — when a supplier is selected the backend renders
  // "Pending Order Statement - <Supplier>" with contact block for email.
  const pdfDownload = async () => {
    try {
      const params = supplierName ? `?supplier=${encodeURIComponent(supplierName)}` : "";
      const res = await api.get(`/reports/pending-purchase-order.pdf${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = supplierName
        ? `Pending Order Statement - ${supplierName}.pdf`
        : `pending-purchase-order-${_todayIso()}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error("PDF download failed"); }
  };

  const openPreview = () => setPreviewOpen(true);
  const closePreview = () => setPreviewOpen(false);
  const co = useMemo(() => getPrintCompany() || COMPANY, []);
  const riskLabel = riskFilter === "all" ? "" : riskFilter === "overdue" ? "Overdue Only" : riskFilter === "due7" ? "Due ≤ 7d" : "Due ≤ 30d";
  const doExport = () => {
    if (!data) return;
    const suffix = supplierName ? `-${supplierName.replace(/[^a-z0-9]+/gi, "_")}` : "";
    if (tab === "rm") {
      exportToCSV(`pending_purchase_by_rm${suffix}.csv`, filtered.rm_rows, [
        { key: "rm_code", label: "RM Code" },
        { key: "rm_name", label: "Name" },
        { key: "uom", label: "UOM" },
        { key: "pending_qty", label: "Pending Qty" },
        { key: "pending_value", label: "Pending Value" },
        { key: "order_count", label: "Order Lines" },
        { key: "earliest_expected_date", label: "Earliest Expected" },
        { key: "suppliers", label: "Suppliers", get: r => (r.suppliers || []).join(" | ") },
        { key: "pos", label: "POs", get: r => (r.pos || []).join(" | ") },
      ]);
    } else {
      exportToCSV(`pending_purchase_by_po${suffix}.csv`, filtered.po_lines, [
        { key: "po_no", label: "PO No" },
        { key: "po_date", label: "PO Date" },
        { key: "expected_date", label: "Expected" },
        { key: "supplier_name", label: "Supplier" },
        { key: "source", label: "Source" },
        { key: "source_ref", label: "Source Ref" },
        { key: "rm_code", label: "RM Code" },
        { key: "rm_name", label: "RM Name" },
        { key: "uom", label: "UOM" },
        { key: "ordered_qty", label: "Ordered" },
        { key: "received_qty", label: "Received" },
        { key: "pending_qty", label: "Pending" },
        { key: "rate", label: "Rate" },
        { key: "pending_value", label: "Pending Value" },
      ]);
    }
  };

  const filteredSupplierOpts = useMemo(() => {
    const s = supplierSearch.trim().toLowerCase();
    return s ? supplierOptions.filter(n => n.toLowerCase().includes(s)) : supplierOptions;
  }, [supplierOptions, supplierSearch]);

  return (
    <div className="p-6 space-y-4" data-testid="pending-purchase-statement-page">
      <div className="flex items-end justify-between flex-wrap gap-3 print-hide">
        <div>
          <div className="overline mb-1">Purchase Reports</div>
          <h1 className="font-display text-3xl font-black tracking-tight">
            {supplierName ? `Pending Order Statement · ${supplierName}` : "Pending Purchase Order"}
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            {supplierName
              ? "Filtered view · every open line for the selected supplier"
              : "Every open PO line · ordered vs received qty · RM-wise roll-up across suppliers"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="pp-refresh" onClick={load} className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm font-semibold rounded-sm flex items-center gap-2"><ArrowsClockwiseIcon size={13} weight="bold" />Refresh</button>
          <button data-testid="pp-export" onClick={doExport} className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm font-semibold rounded-sm flex items-center gap-2"><DownloadSimpleIcon size={13} weight="bold" />Export CSV</button>
          <button data-testid="pp-print" onClick={openPreview} className="h-10 px-4 bg-[var(--accent)] text-white text-sm font-semibold rounded-sm hover:opacity-90 flex items-center gap-2"><PrinterIcon size={13} weight="bold" />View / Print</button>
        </div>
      </div>

      {/* Supplier Filter — searchable dropdown scoped to suppliers with open lines. */}
      <div className="tactical-card p-3 flex items-center gap-3 flex-wrap print-hide" data-testid="pp-supplier-filter-bar">
        <div className="overline text-[10px]">Supplier Filter</div>
        <div className="relative">
          <button
            data-testid="pp-supplier-picker"
            onClick={() => setSupplierPickerOpen(v => !v)}
            className={`h-9 min-w-[280px] px-3 border rounded-sm text-sm font-semibold flex items-center justify-between gap-2 ${supplierName ? "border-[var(--accent)] text-[var(--accent)] bg-[var(--accent)]/10" : "border-[var(--border)] text-zinc-300 hover:bg-[var(--bg-2)]"}`}
          >
            <span className="truncate">{supplierName || "All Suppliers"}</span>
            <CaretDownIcon size={12} weight="bold" />
          </button>
          {supplierPickerOpen && (
            <div data-testid="pp-supplier-dropdown" className="absolute z-30 mt-1 min-w-[320px] bg-[var(--bg-1)] border border-[var(--border)] rounded-sm shadow-xl">
              <div className="p-2 border-b border-[var(--border)]">
                <div className="relative">
                  <MagnifyingGlassIcon size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input
                    autoFocus
                    data-testid="pp-supplier-search"
                    value={supplierSearch}
                    onChange={e => setSupplierSearch(e.target.value)}
                    placeholder="Search supplier…"
                    className="w-full h-8 pl-7 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]"
                  />
                </div>
              </div>
              <div className="max-h-[280px] overflow-y-auto">
                <button
                  data-testid="pp-supplier-opt-all"
                  onClick={() => { setSupplierName(""); setSupplierPickerOpen(false); setSupplierSearch(""); }}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-[var(--bg-2)] ${!supplierName ? "text-[var(--accent)] font-semibold" : "text-zinc-300"}`}
                >
                  All Suppliers <span className="text-zinc-500 text-[10px]">({supplierOptions.length})</span>
                </button>
                {filteredSupplierOpts.length === 0 && <div className="px-3 py-3 text-xs text-zinc-500 italic">No suppliers match.</div>}
                {filteredSupplierOpts.map(n => (
                  <button
                    key={n}
                    data-testid={`pp-supplier-opt-${n}`}
                    onClick={() => { setSupplierName(n); setSupplierPickerOpen(false); setSupplierSearch(""); }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-[var(--bg-2)] ${supplierName === n ? "text-[var(--accent)] font-semibold" : "text-zinc-300"}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        {supplierName && (
          <button data-testid="pp-supplier-clear" onClick={() => setSupplierName("")}
            className="h-8 px-3 text-[10px] font-mono uppercase tracking-widest rounded-sm border border-[var(--border)] hover:bg-[var(--bg-2)] text-zinc-400 flex items-center gap-1">
            <XIcon size={11} weight="bold" />Clear
          </button>
        )}
        {supplierName && selectedSupplier && (
          <div className="text-[10px] text-zinc-400 flex items-center gap-3 flex-wrap" data-testid="pp-supplier-contact">
            {selectedSupplier.email && <span>✉ {selectedSupplier.email}</span>}
            {selectedSupplier.phone && <span>☎ {selectedSupplier.phone}</span>}
            {selectedSupplier.contact_person && <span>👤 {selectedSupplier.contact_person}</span>}
            {selectedSupplier.gstin && <span className="font-mono">GSTIN: {selectedSupplier.gstin}</span>}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 print-hide">
        <PPKpi label="PO Pending Lines" value={fmt(totals.po_line_count || 0, 0)} tone="accent" testId="pp-kpi-lines" />
        <PPKpi label="PO Pending Qty" value={fmtQty(totals.po_pending_qty)} tone="accent" testId="pp-kpi-qty" />
        <PPKpi label="Open / Partial POs" value={fmt(totals.open_partial_po_count || 0, 0)} tone="emerald" testId="pp-kpi-open" />
        <PPKpi label="Raw Materials in Backlog" value={fmt(totals.rm_count || 0, 0)} tone="amber" testId="pp-kpi-rm" />
      </div>

      <div className="tactical-card p-3 flex items-center gap-3 flex-wrap print-hide">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="pp-search" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search PO / Supplier / RM Code / Name…"
            className="w-full h-9 pl-9 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]" />
        </div>
        <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest">
          {["all", "overdue", "due7", "due30"].map(k => (
            <button key={k} data-testid={`pp-risk-${k}`} onClick={() => setRiskFilter(k)}
              className={`h-8 px-3 rounded-sm border ${riskFilter === k ? "border-[var(--accent)] text-[var(--accent)]" : "border-[var(--border)] text-zinc-400 hover:text-white"}`}>
              {k === "all" ? "All" : k === "overdue" ? "Overdue" : k === "due7" ? "Due ≤ 7d" : "Due ≤ 30d"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest">
          <button data-testid="pp-tab-po" onClick={() => setTab("po")}
            className={`h-8 px-3 rounded-sm border ${tab === "po" ? "border-[var(--accent)] text-[var(--accent)]" : "border-[var(--border)] text-zinc-400"}`}>PO Line-wise</button>
          <button data-testid="pp-tab-rm" onClick={() => setTab("rm")}
            className={`h-8 px-3 rounded-sm border ${tab === "rm" ? "border-[var(--accent)] text-[var(--accent)]" : "border-[var(--border)] text-zinc-400"}`}>RM Roll-up</button>
        </div>
      </div>

      <div className="tactical-card overflow-x-auto">
        {loading ? <div className="p-8 text-center text-zinc-500">Loading…</div>
         : tab === "po" ? (
          <table className="w-full text-xs">
            <thead className="bg-[var(--bg-2)]/40 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="px-2 py-2 text-left">PO No</th>
                <th className="px-2 py-2 text-left">Expected</th>
                <th className="px-2 py-2 text-left">Status</th>
                <th className="px-2 py-2 text-left">Supplier</th>
                <th className="px-2 py-2 text-left">RM Code · Name</th>
                <th className="px-2 py-2 text-right">Ordered</th>
                <th className="px-2 py-2 text-right">Received</th>
                <th className="px-2 py-2 text-right">Pending</th>
                <th className="px-2 py-2 text-right">Rate</th>
                <th className="px-2 py-2 text-right">Pending ₹</th>
              </tr>
            </thead>
            <tbody data-testid="pp-po-rows">
              {filtered.po_lines.length > 0 && (() => {
                const t = filtered.po_lines.reduce((a, r) => ({
                  ordered: a.ordered + Number(r.ordered_qty || 0),
                  received: a.received + Number(r.received_qty || 0),
                  pending: a.pending + Number(r.pending_qty || 0),
                  value: a.value + Number(r.pending_value || 0),
                }), { ordered: 0, received: 0, pending: 0, value: 0 });
                return (
                  <tr data-testid="pp-po-total-row" className="border-t-2 border-b-2 border-[var(--accent)] bg-[var(--bg-2)]/60">
                    <td colSpan={5} className="px-2 py-2 uppercase text-[10px] tracking-widest text-zinc-300 font-semibold">Total</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums font-bold text-white">{fmtQty(t.ordered)}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums font-bold text-emerald-300">{fmtQty(t.received)}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums font-bold text-amber-300">{fmtQty(t.pending)}</td>
                    <td></td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums font-bold text-emerald-400">₹{fmt(t.value)}</td>
                  </tr>
                );
              })()}
              {filtered.po_lines.length === 0 ? (
                <tr><td colSpan={10} className="py-8 text-center text-zinc-500" data-testid="pp-empty">No open PO lines — everything received 🎉</td></tr>
              ) : filtered.po_lines.map((r, i) => {
                const st = deliveryStatus(r.expected_date);
                return (
                  <tr key={i} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30">
                    <td className="px-2 py-1.5 font-mono text-[var(--accent)]">{r.po_no}</td>
                    <td className="px-2 py-1.5 font-mono text-[11px]">
                      <div>{r.expected_date || "—"}</div>
                      <div className={`text-[9px] ${st.color}`}>{st.label}</div>
                    </td>
                    <td className="px-2 py-1.5 text-[10px] font-mono uppercase text-zinc-400">{r.status}</td>
                    <td className="px-2 py-1.5">{r.supplier_name || "—"}</td>
                    <td className="px-2 py-1.5">
                      <div className="font-mono text-[11px] text-zinc-200">{r.rm_code}</div>
                      <div className="text-[10px] text-zinc-500">{r.rm_name}</div>
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums">{fmtQty(r.ordered_qty)} <span className="text-[9px] text-zinc-500">{r.uom}</span></td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-emerald-300">{fmtQty(r.received_qty)}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-amber-300 font-semibold">{fmtQty(r.pending_qty)}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums">{fmt(r.rate)}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-emerald-400">{fmt(r.pending_value)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-[var(--bg-2)]/40 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="px-2 py-2 text-left">RM Code</th>
                <th className="px-2 py-2 text-left">Name</th>
                <th className="px-2 py-2 text-left">Earliest Expected</th>
                <th className="px-2 py-2 text-right">Pending Qty</th>
                <th className="px-2 py-2 text-right">Pending ₹</th>
                <th className="px-2 py-2 text-right">Order Lines</th>
                <th className="px-2 py-2 text-left">Suppliers</th>
                <th className="px-2 py-2 text-left">POs</th>
              </tr>
            </thead>
            <tbody data-testid="pp-rm-rows">
              {filtered.rm_rows.length > 0 && (() => {
                const t = filtered.rm_rows.reduce((a, r) => ({
                  pending: a.pending + Number(r.pending_qty || 0),
                  value: a.value + Number(r.pending_value || 0),
                  lines: a.lines + Number(r.order_count || 0),
                }), { pending: 0, value: 0, lines: 0 });
                return (
                  <tr data-testid="pp-rm-total-row" className="border-t-2 border-b-2 border-[var(--accent)] bg-[var(--bg-2)]/60">
                    <td colSpan={3} className="px-2 py-2 uppercase text-[10px] tracking-widest text-zinc-300 font-semibold">Total</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums font-bold text-amber-300">{fmtQty(t.pending)}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums font-bold text-emerald-400">₹{fmt(t.value)}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums font-bold text-white">{t.lines}</td>
                    <td colSpan={2}></td>
                  </tr>
                );
              })()}
              {filtered.rm_rows.length === 0 ? (
                <tr><td colSpan={8} className="py-8 text-center text-zinc-500">No pending RMs.</td></tr>
              ) : filtered.rm_rows.map((r, i) => {
                const st = deliveryStatus(r.earliest_expected_date);
                return (
                  <tr key={i} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30">
                    <td className="px-2 py-1.5 font-mono text-[var(--accent)]">{r.rm_code || "—"}</td>
                    <td className="px-2 py-1.5">{r.rm_name || "—"}</td>
                    <td className="px-2 py-1.5 font-mono text-[11px]">
                      <div>{r.earliest_expected_date || "—"}</div>
                      <div className={`text-[9px] ${st.color}`}>{st.label}</div>
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-amber-300 font-semibold">{fmtQty(r.pending_qty)} <span className="text-[9px] text-zinc-500">{r.uom}</span></td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-emerald-400">{fmt(r.pending_value)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.order_count}</td>
                    <td className="px-2 py-1.5 text-[10px] text-zinc-400">{(r.suppliers || []).join(", ")}</td>
                    <td className="px-2 py-1.5 text-[10px] font-mono text-zinc-400">{(r.pos || []).join(", ")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {previewOpen && (
        <PPPreviewOverlay
          company={co}
          supplier={selectedSupplier}
          generatedAt={data?.generated_at}
          poLines={filtered.po_lines}
          rmRows={filtered.rm_rows}
          totals={totals}
          riskLabel={riskLabel}
          onClose={closePreview}
          onPrint={doPrint}
        />
      )}
    </div>
  );
}

function PPPreviewOverlay({ company, supplier, generatedAt, poLines, rmRows, totals, riskLabel, onClose, onPrint }) {  const title = supplier ? `Pending Order Statement · ${supplier.name}` : "Pending Purchase Order";
  return (
    <div className="fixed inset-0 z-[10000] flex flex-col" data-testid="pp-preview-overlay">
      <div className="absolute inset-0 bg-black/80 print-hide" />
      <div className="relative flex items-center justify-between px-6 py-3 bg-[var(--bg-1)] border-b border-[var(--border)] print-hide">
        <div>
          <div className="overline text-[10px]">Print Preview · A4 Landscape{riskLabel ? ` · ${riskLabel}` : ""}</div>
          <div className="font-display text-sm font-bold text-white">{title}</div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="pp-preview-print" onClick={onPrint} className="h-9 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2">
            <PrinterIcon size={13} weight="bold" />Print / PDF
          </button>
          <button data-testid="pp-preview-close" onClick={onClose} className="h-9 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm font-semibold rounded-sm flex items-center gap-2">
            <XIcon size={13} weight="bold" />Close
          </button>
        </div>
      </div>
      <div className="relative flex-1 overflow-auto p-6 bg-zinc-800">
        <PPPrintSheet company={company} supplier={supplier} generatedAt={generatedAt} poLines={poLines} rmRows={rmRows} totals={totals} riskLabel={riskLabel} />
      </div>
    </div>
  );
}

function PPPrintSheet({ company, supplier, generatedAt, poLines, rmRows, totals, riskLabel }) {
  const poTotals = poLines.reduce((a, r) => ({
    ordered: a.ordered + Number(r.ordered_qty || 0),
    received: a.received + Number(r.received_qty || 0),
    pending: a.pending + Number(r.pending_qty || 0),
    value: a.value + Number(r.pending_value || 0),
  }), { ordered: 0, received: 0, pending: 0, value: 0 });
  return (
    <div id="pp-print-sheet" className="mx-auto bg-white text-black shadow-2xl" style={{ width: "1123px", minHeight: "794px", padding: "32px 36px" }}>
      <div className="text-center border-b-2 border-black pb-2 mb-3">
        <div className="font-black text-xl uppercase tracking-wide">{company.name}</div>
        <div className="text-[10px] whitespace-pre-line">{company.address}</div>
        <div className="text-lg font-bold mt-2 uppercase">
          {supplier ? `Pending Order Statement · ${supplier.name}` : "Pending Purchase Order Statement"}
        </div>
        <div className="text-[10px]">Generated: {new Date(generatedAt || Date.now()).toLocaleString("en-IN")}{riskLabel ? ` · Filter: ${riskLabel}` : ""}</div>
      </div>

      {/* Supplier contact block — visible only when filtered so the PDF is
          email-ready ("To: <supplier>, cc contact person"). */}
      {supplier && (
        <div className="flex justify-between items-start border border-black rounded-sm p-2 mb-3 text-[10px]">
          <div>
            <div className="text-[8px] uppercase tracking-widest text-gray-600">Supplier</div>
            <div className="font-bold text-[12px]">{supplier.name}</div>
            {supplier.contact_person && <div>Attn: {supplier.contact_person}</div>}
            {supplier.address && <div className="whitespace-pre-line">{supplier.address}</div>}
          </div>
          <div className="text-right">
            {supplier.email && <div>✉ {supplier.email}</div>}
            {supplier.phone && <div>☎ {supplier.phone}</div>}
            {supplier.gstin && <div className="font-mono">GSTIN: {supplier.gstin}</div>}
          </div>
        </div>
      )}

      <div className="flex justify-around border border-black py-1.5 mb-3 text-[10px]">
        <div><b>Open PO Lines:</b> {totals.po_line_count || poLines.length}</div>
        <div><b>Pending Qty:</b> {fmtQty(poTotals.pending)}</div>
        <div><b>Pending ₹:</b> {fmt(poTotals.value)}</div>
        <div><b>Unique RMs:</b> {totals.rm_count || rmRows.length}</div>
      </div>

      <div style={{ pageBreakInside: "avoid" }} className="mt-2">
        <div className="text-[11px] font-bold uppercase tracking-widest mb-1 border-b border-black pb-0.5">PO Line-wise Pending ({poLines.length} lines)</div>
        <table className="w-full text-[9px] border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="border border-gray-400 px-1.5 py-1 text-left">PO No</th>
              <th className="border border-gray-400 px-1.5 py-1 text-left">Expected</th>
              {!supplier && <th className="border border-gray-400 px-1.5 py-1 text-left">Supplier</th>}
              <th className="border border-gray-400 px-1.5 py-1 text-left" style={{ width: "26%" }}>RM Code · Name</th>
              <th className="border border-gray-400 px-1.5 py-1 text-right">Ordered</th>
              <th className="border border-gray-400 px-1.5 py-1 text-right">Received</th>
              <th className="border border-gray-400 px-1.5 py-1 text-right">Pending</th>
              <th className="border border-gray-400 px-1.5 py-1 text-right">Rate</th>
              <th className="border border-gray-400 px-1.5 py-1 text-right">Pending ₹</th>
            </tr>
          </thead>
          <tbody>
            {poLines.length === 0 && <tr><td colSpan={supplier ? 8 : 9} className="border border-gray-400 px-2 py-3 text-center italic">No open PO lines.</td></tr>}
            {poLines.map((r, i) => (
              <tr key={i}>
                <td className="border border-gray-400 px-1.5 py-1 font-mono">{r.po_no}</td>
                <td className="border border-gray-400 px-1.5 py-1">{r.expected_date || "—"}</td>
                {!supplier && <td className="border border-gray-400 px-1.5 py-1">{r.supplier_name || "—"}</td>}
                <td className="border border-gray-400 px-1.5 py-1"><div className="font-mono" style={{ wordBreak: "break-all" }}>{r.rm_code}</div><div className="text-[8px] text-gray-600">{r.rm_name}</div></td>
                <td className="border border-gray-400 px-1.5 py-1 text-right font-mono tabular-nums">{fmtQty(r.ordered_qty)} {r.uom}</td>
                <td className="border border-gray-400 px-1.5 py-1 text-right font-mono tabular-nums">{fmtQty(r.received_qty)}</td>
                <td className="border border-gray-400 px-1.5 py-1 text-right font-mono tabular-nums font-bold">{fmtQty(r.pending_qty)}</td>
                <td className="border border-gray-400 px-1.5 py-1 text-right font-mono tabular-nums">{fmt(r.rate)}</td>
                <td className="border border-gray-400 px-1.5 py-1 text-right font-mono tabular-nums font-bold">{fmt(r.pending_value)}</td>
              </tr>
            ))}
            {poLines.length > 0 && (
              <tr className="bg-gray-300 font-bold border-y-2 border-black">
                <td colSpan={supplier ? 3 : 4} className="border border-gray-400 px-1.5 py-1 uppercase">Sum Total</td>
                <td className="border border-gray-400 px-1.5 py-1 text-right font-mono tabular-nums">{fmtQty(poTotals.ordered)}</td>
                <td className="border border-gray-400 px-1.5 py-1 text-right font-mono tabular-nums">{fmtQty(poTotals.received)}</td>
                <td className="border border-gray-400 px-1.5 py-1 text-right font-mono tabular-nums">{fmtQty(poTotals.pending)}</td>
                <td className="border border-gray-400 px-1.5 py-1"></td>
                <td className="border border-gray-400 px-1.5 py-1 text-right font-mono tabular-nums">{fmt(poTotals.value)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ pageBreakInside: "avoid" }} className="mt-4">
        <div className="text-[11px] font-bold uppercase tracking-widest mb-1 border-b border-black pb-0.5">RM-wise Aggregate ({rmRows.length} codes)</div>
        <table className="w-full text-[10px] border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="border border-gray-400 px-2 py-1 text-left" style={{ width: "22%" }}>RM Code</th>
              <th className="border border-gray-400 px-2 py-1 text-left">Name</th>
              <th className="border border-gray-400 px-2 py-1 text-left">Earliest Expected</th>
              <th className="border border-gray-400 px-2 py-1 text-right">Pending Qty</th>
              <th className="border border-gray-400 px-2 py-1 text-right">Pending ₹</th>
              <th className="border border-gray-400 px-2 py-1 text-center">Lines</th>
              {!supplier && <th className="border border-gray-400 px-2 py-1 text-left">Suppliers</th>}
            </tr>
          </thead>
          <tbody>
            {rmRows.length === 0 && <tr><td colSpan={supplier ? 6 : 7} className="border border-gray-400 px-2 py-3 text-center italic">No pending RMs.</td></tr>}
            {rmRows.map((r, i) => (
              <tr key={i}>
                <td className="border border-gray-400 px-2 py-1 font-mono" style={{ wordBreak: "break-all" }}>{r.rm_code}</td>
                <td className="border border-gray-400 px-2 py-1">{r.rm_name}</td>
                <td className="border border-gray-400 px-2 py-1">{r.earliest_expected_date || "—"}</td>
                <td className="border border-gray-400 px-2 py-1 text-right font-mono tabular-nums font-bold">{fmtQty(r.pending_qty)} {r.uom}</td>
                <td className="border border-gray-400 px-2 py-1 text-right font-mono tabular-nums font-bold">{fmt(r.pending_value)}</td>
                <td className="border border-gray-400 px-2 py-1 text-center font-mono">{r.order_count}</td>
                {!supplier && <td className="border border-gray-400 px-2 py-1">{(r.suppliers || []).join(", ") || "—"}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 pt-2 border-t border-black text-[9px] text-gray-700 flex justify-between">
        <span>Confidential · For internal use only</span>
        <span>A4 · Landscape</span>
      </div>
    </div>
  );
}

// Feb 2026 · Purchase KPI tile — mirrors the Pending Sales Order KPI card
// (rounded-xl, bg-[var(--bg-1)] surface, overline label, big display value).
function PPKpi({ label, value, tone = "accent", testId }) {
  const color = { accent: "text-[var(--accent)]", emerald: "text-emerald-400", amber: "text-amber-400" }[tone];
  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-1)] p-4" data-testid={testId}>
      <div className="overline text-[10px]">{label}</div>
      <div className={`font-display font-black text-2xl mt-1 tabular-nums ${color}`}>{value ?? 0}</div>
    </div>
  );
}

